import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { isEqual } from 'lodash';

import type { VideaNotesAppointmentData } from './types';
import { VideaNotesUIState, VoiceNotesSource } from './types';

// these are all the valid statuses provided by the react-media-recorder library
export const recorderStatusAtom = atom<
  | 'idle'
  | 'recording'
  | 'stopped'
  | 'acquiring_media'
  | 'media_aborted'
  | 'paused'
  | 'permission_denied'
  | 'no_specified_media_found'
  | 'media_in_use'
  | 'invalid_media_constraints'
  | 'no_constraints'
  | 'recorder_error'
  | 'delayed_start'
  | 'stopping'
>('idle');

export const voiceNotesSourceAtom = atom<VoiceNotesSource>(
  VoiceNotesSource.VOICE_NOTES_BUTTON,
);

// these are all the valid states that the Videa Notes UI can exist in
export const uiStatusAtom = atom<VideaNotesUIState>(
  VideaNotesUIState.AUDIO_CAPTURE,
);

export const isRecordingSessionActiveAtom = atom((get) => {
  const status = get(recorderStatusAtom);
  return status === 'recording' || status === 'paused';
});

export const isRecordingSessionPausedAtom = atom((get) => {
  const status = get(recorderStatusAtom);
  return status === 'paused';
});

export const isNoteGenerationActiveAtom = atom((get) => {
  const uiStatus = get(uiStatusAtom);
  return uiStatus === VideaNotesUIState.NOTE_GENERATION;
});

// indirect control visibility of the dev-tools widget
export const isRecorderSheetOpenAtom = atom(false);
export const shouldReopenRecorderSheetAtom = atom(false);
export const isActiveAnnotatorRouteAtom = atom(false);
export const isCancelRecordingConfirmationShownAtom = atom(false);

// the appointment that the recorder relates to
export const appointmentDataAtom = atom<VideaNotesAppointmentData | null>(null);

// the currently selected note ID (persists across sheet open/close)
export const selectedNoteIdAtom = atom<string | undefined>(undefined);

// A reactive atom that updates appointment data and
// resets appointment-specific recording data
export const appointmentDataWithResetAtom = atom(
  (get) => get(appointmentDataAtom), // allow reading
  (get, set, newData: VideaNotesAppointmentData | null) => {
    const prevData = get(appointmentDataAtom);

    // Check if the data has actually changed using deep equality
    const isDataChanged = !isEqual(prevData, newData);

    if (isDataChanged) {
      set(appointmentDataAtom, newData);
      set(mediaBlobUrlAtom, undefined); // reset audio blob
      set(stoppedRecordingDurationAtom, 0);
      set(selectedNoteIdAtom, undefined); // reset selected note when appointment changes
    }
  },
);

// our local audio recording location, when it's not undefined
export const mediaBlobUrlAtom = atom<string | undefined>(undefined);

// store uploaded audio file (when user uploads instead of recording)
// This allows preserving the original file metadata (type, name) for upload
export const uploadedAudioFileAtom = atom<File | null>(null);

// when we're actively recording, this holds the audio stream preview, which we're using
// for visualizations
export const previewAudioStreamAtom = atom<MediaStream | null>(null);

// when we're actively recording, this holds the audio stream preview, which we're using
// for visualizations
export const monitoringStreamAtom = atom<MediaStream | null>(null);

// track if actively recording
export const isRecordingAtom = atom(false);
export const isPausedAtom = atom(false);

export const recordingStartTimeAtom = atom<number | null>(null); // timestamp
export const accumulatedDurationAtom = atom<number>(0); // total time before last pause
export const stoppedRecordingDurationAtom = atom<number | null>(null); // show duration of a stopped recording

// microphone device selection
export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
}

export const availableAudioDevicesAtom = atom<AudioDevice[]>([]);
export const selectedAudioDeviceIdAtom = atomWithStorage<string | null>(
  'videa-notes-selected-microphone',
  null,
);
export const isDeviceSelectionOpenAtom = atom(false);
export const hasMicrophonePermissionAtom = atom<boolean>(false);
export const shouldAllowRecordingAtom = atom<boolean>(false);

// ticking every second - forces recordingDurationAtom to re-evaluate
export const tickAtom = atom<number>(0);

// derived atom: total elapsed (live updating)
export const recordingDurationAtom = atom((get) => {
  get(tickAtom); // forces recalculation
  const isRecording = get(isRecordingAtom);
  const startTime = get(recordingStartTimeAtom);
  const accumulated = get(accumulatedDurationAtom);

  if (isRecording && startTime) {
    return accumulated + Math.floor((Date.now() - startTime) / 1000);
  }

  return accumulated;
});

// start (or resume) recording
export const startRecordingAtom = atom(null, (get, set) => {
  // Don't reset timer if already recording
  if (get(isRecordingAtom)) {
    return;
  }

  set(stoppedRecordingDurationAtom, null);
  set(recordingStartTimeAtom, Date.now());
  set(isRecordingAtom, true);
  set(isPausedAtom, false);
});

// pause recording
export const pauseRecordingAtom = atom(null, (get, set) => {
  const startTime = get(recordingStartTimeAtom);
  const accumulatedTime = get(accumulatedDurationAtom);

  if (startTime) {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    set(accumulatedDurationAtom, accumulatedTime + elapsed);
  }

  set(isRecordingAtom, false);
  set(isPausedAtom, true);
  set(recordingStartTimeAtom, null);
});

// stop recording (full stop, not paused)
export const stopRecordingAtom = atom(null, (get, set) => {
  set(isRecordingAtom, false);
  set(isPausedAtom, false);
  set(recordingStartTimeAtom, null);
  set(accumulatedDurationAtom, 0);
  set(stoppedRecordingDurationAtom, null);

  // Get current streams before clearing
  const previewStream = get(previewAudioStreamAtom);
  const monitoringStream = get(monitoringStreamAtom);

  // Stop all tracks in preview stream
  if (previewStream) {
    previewStream.getTracks().forEach((track) => track.stop());
  }

  // Stop all tracks in monitoring stream
  if (monitoringStream) {
    monitoringStream.getTracks().forEach((track) => track.stop());
  }

  set(previewAudioStreamAtom, null);
  set(monitoringStreamAtom, null);
});
